/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the invitation page component
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import { faChevronDown, faChevronUp } from '@fortawesome/pro-duotone-svg-icons';
import { faStar } from '@fortawesome/pro-regular-svg-icons';
import { faStar as solidStar } from '@fortawesome/pro-solid-svg-icons';
import { Box, Button, ButtonBase, Divider, Tooltip, Typography, darken, lighten, styled } from '@mui/material';
import * as React from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import whiteLogo from '@/assets/img/bctNoLabel-w.png';
import colorLogo from '@/assets/img/bctNoLabel.png';

import Icon from '@/components/layouts/Icon/Icon';
import ChosenThemeContext from '@/contexts/styles/ChosenThemeContext';
import { useContext } from 'react';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface HeaderBoxProps {
    pointerEnabled?: boolean;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const HeaderBox = styled(Box, { shouldForwardProp: (prop) => prop !== 'pointerEnabled'  })<HeaderBoxProps>(({ theme, pointerEnabled }) => ({
    padding: theme.spacing(2),
    display: 'flex',
    maxHeight: '96px',
    gap: theme.spacing(2),
    '&:hover .logo button': {
        opacity: 1
    },
    position: 'relative',
    ":hover": {
        cursor: pointerEnabled ? 'pointer' : 'default'
    },
    overflow: 'hidden'
}));

const LeftSideHeaderBox = styled(Box)(() => ({
    width: '64px',
    height: '64px',
    flex: '0 0 auto',
    alignSelf: 'center',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    userSelect: 'none'
}));

const AppearingButton = styled(ButtonBase)(({ theme }) => ({

    position: 'absolute',
    borderRadius: '50%',
    background: theme.palette.mode === 'dark' ? darken(theme.palette.primary.main,0.5) : lighten(theme.palette.primary.main, 0.5),

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    opacity: 0,
    transition: 'opacity 0.5s linear'
}));

const RightSideHeaderBox = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: '1 1 auto',
    justifyContent: 'space-around',
    minWidth: '60%',
    position: 'relative',
    zIndex: 1
}));

const TitleStarBox = styled(Box)(({ theme }) => ({
    flex: '1 1 50%',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
}));

const TitleBox = styled(Box)(() => ({
    alignSelf: 'center',
    flexShrink: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'keep-all'
}));

const StarBox = styled(Box)(() => ({
    alignSelf: 'top',
    flexShrink: 0,
    position: 'absolute', // Add this line
    top: 16, // Add this line
    right: 16, // Add this line
}));

const StarButton = styled(Button)(({ theme }) => ({
    minWidth: 'unset',
    padding: theme.spacing(1),
    borderRadius: '50%',
    marginTop: theme.spacing(-1.5),
    marginRight: theme.spacing(-1.5),
    flexShrink: 0
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */
const TileHeader: React.FunctionComponent<{
    trialId: number;
    trialName: string; 
    protocolId: string; 
    favourite: boolean;
    expanded: boolean;
    logoLightUrl?: string;
    
    logoDarkUrl?: string;
    logoDesktopHeight?: string;
    logoDesktopWidth?: string;
    logoMobileHeight?: string;
    logoMobileWidth?: string;
    toggle?: () => void;
    toggleFavourite: (id: number) => void;
}> = ({
    trialId,
    trialName,
    protocolId,
    favourite,
    expanded,
    logoLightUrl,
    logoDarkUrl,
    logoDesktopHeight,
    logoDesktopWidth,
    logoMobileHeight,
    logoMobileWidth,
    toggle,
    toggleFavourite,
}) => {
    const chosenTheme = useContext(ChosenThemeContext);

    const logoContainerRef = React.useRef<HTMLDivElement>(null);

    const [hoverPosition, setHoverPosition] = React.useState<{ maxDimension: number; left: number; top: number;}>({ 
        maxDimension: 64, 
        left: 0, 
        top: 0
    });

    React.useEffect(() => {
        const updateMaxDimension = () => {
            if (logoContainerRef.current) {
                const width = logoContainerRef.current.clientWidth;
                const height = logoContainerRef.current.clientHeight;
                const maxDimension = Math.max(width, height) + 10;

                setHoverPosition({ 
                    maxDimension, 
                    left: (width - maxDimension) / 2, 
                    top: (height - maxDimension) / 2
                });
            }
            else {
                setHoverPosition({ maxDimension: 64, left: 0, top: 0 });
            }
        };

        updateMaxDimension();

        window.addEventListener('resize', updateMaxDimension);

        return () => {
            window.removeEventListener('resize', updateMaxDimension);
        };
    }, [logoContainerRef, setHoverPosition, logoDesktopWidth, logoDesktopHeight, logoMobileWidth, logoMobileHeight]);

    return (
        <>
            <HeaderBox
                onClick={toggle}
                pointerEnabled={!favourite}
            >
                <LeftSideHeaderBox
                    ref={logoContainerRef}
                    className="logo"
                    sx={{
                        width: {
                            xs: logoMobileWidth, 
                            sm: logoMobileWidth,
                            md: logoDesktopWidth,
                            lg: logoDesktopWidth,
                            xl: logoDesktopWidth
                        },
                        height: {
                            xs: logoMobileHeight,
                            sm: logoMobileHeight,
                            md: logoDesktopHeight,
                            lg: logoDesktopHeight,
                            xl: logoDesktopHeight
                        }
                    }}
                >
                    <Box 
                        sx={{
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(${chosenTheme === 'dark' ? logoDarkUrl ??  whiteLogo : logoLightUrl ?? colorLogo})`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center'
                        }}
                    />
                    {/* <Image
                        // src={`https://source.unsplash.com/random/64x64?Bear&t=${time}`}
                        
                        src={ chosenTheme === 'dark' ? logoDarkUrl ??  whiteLogo : logoLightUrl ?? colorLogo }
                        alt={"BCT Logo"}
                        sx={{
                            width: {
                                xs: logoMobileWidth, 
                                sm: logoMobileWidth,
                                md: logoDesktopWidth,
                                lg: logoDesktopWidth,
                                xl: logoDesktopWidth
                            },
                            height: {
                                xs: logoMobileHeight,
                                sm: logoMobileHeight,
                                md: logoDesktopHeight,
                                lg: logoDesktopHeight,
                                xl: logoDesktopHeight
                            }
                        }}
                    /> */}
                    {
                        toggle && (
                            <AppearingButton
                                sx={{
                                    width: hoverPosition.maxDimension,
                                    height: hoverPosition.maxDimension,
                                    left: hoverPosition.left,
                                    top: hoverPosition.top
                                }}
                                // onClick={toggle} // This is not needed as the parent has the onClick event
                            >
                                <Icon fixedWidth icon={expanded ? faChevronUp : faChevronDown } />
                            </AppearingButton>
                        )
                    }
                </LeftSideHeaderBox>
                <RightSideHeaderBox>
                    <TitleStarBox>
                        <TitleBox>
                            <Typography variant="h6">
                                {trialName}
                            </Typography>
                            <Typography variant='caption'>
                                {protocolId}
                            </Typography>
                        </TitleBox>
                        
                        <StarBox>
                            <Tooltip 
                                title={favourite ? 'Remove from favourites' : 'Add to favourites'}
                            >
                                <StarButton
                                    onClick={() => toggleFavourite(trialId)}
                                >
                                    <Icon
                                        icon={ favourite ? solidStar : faStar} 
                                        fixedWidth 
                                    />
                                </StarButton>
                        </Tooltip>
                        </StarBox>
                    </TitleStarBox>
                </RightSideHeaderBox>
                
            </HeaderBox>
            {expanded && <Divider />}
        </>
        

    );
};


/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default TileHeader;